import axios from 'axios';
import { ROUTES } from '@/constants/routes';
import { extractIdentifiers, filterAndMapObjects } from '@/utils/menuUtils.js';
import { navigationMenus } from '@/constants/navigationMenus';

export const sosHttpInstance = axios.create({
  baseURL: import.meta.env.VITE_ROOT_URL, // Replace with your API base URL
  timeout: 300000,
  headers: {
    'Cache-Control': 'no-store, no-cache, must-revalidate, proxy-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
    'Surrogate-Control': 'no-store',
  },
});

/** This function clears all caches in the browser
 It is called when the user logs out or when the authentication token is refreshed */
function clearCaches() {
  if ('caches' in window) {
    caches.keys().then((cacheNames) => {
      cacheNames.forEach((cacheName) => {
        caches.delete(cacheName);
      });
    });
  }
}

sosHttpInstance.interceptors.request.use(
  function (config) {
    // Retrieve tokens from localStorage
    const authToken = localStorage.getItem('Authentication');
    const authorizationToken = localStorage.getItem('Authorization');
    const refreshToken = localStorage.getItem('Refresh');
    // Check if tokens are available and set them in headers
    if (authToken) {
      config.headers['Authentication'] = `${authToken}`;
    }
    if (authorizationToken) {
      config.headers['Authorization'] = `${authorizationToken}`;
    }
    if (refreshToken) {
      config.headers['Refresh'] = `${refreshToken}`;
    }

    return config;
  },
  function (error) {
    // Handle request error
    return Promise.reject(error);
  },
);

sosHttpInstance.interceptors.response.use(
  function (response) {
    // Any status code that lies within the range of 2xx causes this function to trigger
    if (response && response.status === 201) {
      localStorage.removeItem('Authentication');
      localStorage.removeItem('Authorization');
      clearCaches();
      localStorage.setItem('Authentication', response.data.authenticationToken);
      localStorage.setItem('Authorization', response.data.authorizationToken);

      localStorage.setItem(
        'FeatureList',
        JSON.stringify(response.data.featureDetails),
      );

      const identifiers = extractIdentifiers(response.data.featureDetails);

      const menuItems = filterAndMapObjects(
        navigationMenus.navigation,
        identifiers,
      );
      localStorage.setItem('menuItems', JSON.stringify(menuItems));
    }

    return response;
  },
  function (error) {
    // Any status codes that fall outside the range of 2xx cause this function to trigger
    if (error.response && error.response.status === 301) {
      // Clear local storage
      localStorage.clear();
      clearCaches();
      // Reload the page
      window.location.href = import.meta.env.VITE_MICROSOFT_LOGOUT_URL;
    }
    if (error.response && error.response.status === 401) {
      // Log 401 errors but don't redirect - let components handle API errors
      // Only clear tokens if this is an auth endpoint failure
      console.warn('401 Unauthorized error from API:', error.config?.url);

      // Check if this is an authentication/authorization endpoint failure
      const isAuthEndpoint =
        error.config?.url?.includes('/auth') ||
        error.config?.url?.includes('/authorization');

      if (isAuthEndpoint) {
        // Only redirect if authentication service itself failed
        console.error('Authentication service failed, redirecting...');
        localStorage.clear();
        clearCaches();
        window.location.href = ROUTES.UNAUTHORIZED;
      }
      // Otherwise, let the component handle the error (e.g., show error message)
    }
    return Promise.reject(error);
  },
);

export const agentHttpInstance = axios.create({
  baseURL: import.meta.env.VITE_ROOT_URL,
  timeout: 300000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Auth HTTP Instance - For initial authentication requests
 * No auth headers sent (used for login/token generation)
 */
export const authHttpInstance = axios.create({
  baseURL: import.meta.env.VITE_ROOT_URL,
  timeout: 300000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** This function clears all caches in the browser
 It is called when the user logs out or when the authentication token is refreshed */
